import { useEffect, useRef, useState } from "react";
import { Routes, Route, Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ConservationPill from "@/components/ConservationPill";
import { streamAI } from "@/lib/stream";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";
import { Plus, ShieldCheck, Upload, FileText, CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";

const MAX_FILE_MB = 10;

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  return bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function FileDrop({ testid, accept, multiple = false, disabled = false, busy = false, busyLabel = "Working…", busyTestid, hint, onFiles }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const exts = accept.split(",").map((s) => s.trim().toLowerCase());
  const inactive = disabled || busy;

  const handle = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const ok = [];
    for (const f of files) {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!exts.includes(ext)) { toast.error(`${f.name}: unsupported file type`); continue; }
      if (f.size > MAX_FILE_MB * 1048576) { toast.error(`${f.name}: exceeds the ${MAX_FILE_MB} MB limit`); continue; }
      ok.push(f);
    }
    if (ok.length) onFiles(multiple ? ok : ok.slice(0, 1));
  };

  return (
    <div
      data-testid={`${testid}-dropzone`}
      role="button"
      tabIndex={0}
      aria-disabled={inactive}
      onClick={() => !inactive && inputRef.current?.click()}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !inactive) inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); if (!inactive) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!inactive) handle(e.dataTransfer.files); }}
      className={`mt-1 flex items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-5 text-xs transition-colors ${dragOver ? "border-[color:var(--forest-deep)] bg-emerald-50" : "border-stone-300 bg-stone-50/60 hover:border-stone-400"} ${inactive ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <input ref={inputRef} data-testid={testid} type="file" accept={accept} multiple={multiple} className="hidden"
             disabled={inactive} onChange={(e) => { handle(e.target.files); e.target.value = ""; }} />
      {busy ? (
        <span data-testid={busyTestid || `${testid}-busy`} className="mono text-stone-500 animate-pulse">{busyLabel}</span>
      ) : (
        <>
          <Upload className="h-4 w-4 text-stone-400" />
          <span className="text-stone-500">{hint}</span>
        </>
      )}
    </div>
  );
}

function Dashboard() {
  const { user } = useAuth();
  const { fmt } = useCurrency();
  const [lots, setLots] = useState([]);
  const [orders, setOrders] = useState([]);
  const [acc, setAcc] = useState([]);

  useEffect(() => {
    api.get("/lots/mine").then(r => setLots(r.data.lots));
    api.get("/orders/mine").then(r => setOrders(r.data.orders));
    api.get("/seller/accreditation/me").then(r => setAcc(r.data.records));
  }, []);

  const verified = user?.accreditation_status === "verified";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="heading text-3xl font-extrabold tracking-tight">Seller Studio</h1>
          <p className="text-stone-600 mt-1">{user?.organization || user?.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs mono uppercase border ${verified ? "pill-recovering" : user?.accreditation_status === "pending" ? "pill-watch" : "pill-critical"}`}>
            <ShieldCheck className="h-3.5 w-3.5" /> {user?.accreditation_status}
          </span>
          <Link to="/seller/new-lot">
            <Button data-testid="seller-new-lot-btn" disabled={!verified} className="btn-earth"><Plus className="h-4 w-4 mr-1" /> New lot</Button>
          </Link>
        </div>
      </div>

      <Tabs defaultValue="lots" className="mt-8">
        <TabsList data-testid="seller-tabs">
          <TabsTrigger value="lots" data-testid="tab-lots">My lots ({lots.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="accreditation" data-testid="tab-accreditation">Accreditation</TabsTrigger>
        </TabsList>
        <TabsContent value="lots" className="mt-4">
          <div className="surface-card overflow-hidden">
            <table className="data-table">
              <thead><tr><th>Lot</th><th>Species / Breed</th><th>Status</th><th>Qty</th><th>Price</th></tr></thead>
              <tbody>
                {lots.map(l => (
                  <tr key={l.lot_id}><td className="mono text-xs">{l.lot_id}</td>
                    <td><div className="font-medium">{l.title}</div><div className="text-xs text-stone-500">{l.species} · {l.breed}</div></td>
                    <td><ConservationPill status={l.conservation_status} /></td>
                    <td className="mono">{l.quantity}</td>
                    <td className="mono">{fmt(l.price_per_unit)}</td>
                  </tr>
                ))}
                {lots.length === 0 && <tr><td colSpan={5} className="text-stone-500 text-center py-6">No lots yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="orders" className="mt-4">
          <OrderList orders={orders} />
        </TabsContent>
        <TabsContent value="accreditation" className="mt-4">
          <AccreditationSection records={acc} onSubmitted={(r) => setAcc([r, ...acc])} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrderList({ orders }) {
  const { fmt } = useCurrency();
  const { user } = useAuth();
  return (
    <div className="surface-card overflow-hidden">
      <table className="data-table">
        <thead><tr><th>Order</th><th>Lot</th><th>Qty</th><th>Total</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {orders.filter(o => o.seller_id === user?.user_id).map(o => (
            <tr key={o.order_id}>
              <td className="mono text-xs">{o.order_id}</td>
              <td>{o.lot_title}</td>
              <td className="mono">{o.quantity}</td>
              <td className="mono">{fmt(o.total)}</td>
              <td><span className="text-xs mono uppercase px-2 py-1 rounded-full pill-watch">{o.status}</span></td>
              <td><Link to={`/orders/${o.order_id}`} className="text-[color:var(--forest-deep)] underline text-sm">Open</Link></td>
            </tr>
          ))}
          {orders.length === 0 && <tr><td colSpan={6} className="text-stone-500 text-center py-6">No orders yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AccreditationSection({ records, onSubmitted }) {
  const [form, setForm] = useState({ organization: "", license_number: "", country: "USA", lab_certifications: "USDA-APHIS", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, lab_certifications: form.lab_certifications.split(",").map(s => s.trim()).filter(Boolean) };
      const { data } = await api.post("/seller/accreditation", payload);
      toast.success("Accreditation submitted — awaiting admin review");
      onSubmitted(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="surface-card p-6 space-y-3">
        <h3 className="heading font-semibold">Submit accreditation</h3>
        <div><Label>Organization</Label><Input data-testid="acc-org-input" required value={form.organization} onChange={on("organization")} /></div>
        <div><Label>License number</Label><Input data-testid="acc-license-input" required value={form.license_number} onChange={on("license_number")} /></div>
        <div><Label>Country</Label><Input value={form.country} onChange={on("country")} /></div>
        <div><Label>Lab certifications (comma-separated)</Label><Input value={form.lab_certifications} onChange={on("lab_certifications")} placeholder="USDA-APHIS, ICAR" /></div>
        <div><Label>Notes</Label><Textarea value={form.notes} onChange={on("notes")} /></div>
        <Button data-testid="acc-submit-btn" type="submit" className="btn-forest">Submit for review</Button>
      </form>
      <div className="surface-card p-6">
        <h3 className="heading font-semibold">History</h3>
        {records.length === 0 ? <div className="text-stone-500 text-sm mt-2">No submissions yet.</div> :
          <ul className="mt-3 space-y-2 text-sm">
            {records.map((r, i) => (
              <li key={i} className="flex items-center justify-between border-b py-2">
                <div><div className="font-medium">{r.organization}</div><div className="text-xs text-stone-500 mono">{r.license_number}</div></div>
                <span className={`text-xs mono uppercase px-2 py-1 rounded-full ${r.status === "verified" ? "pill-recovering" : r.status === "rejected" ? "pill-critical" : "pill-watch"}`}>{r.status}</span>
              </li>
            ))}
          </ul>}
      </div>
    </div>
  );
}

function NewLot() {
  const nav = useNavigate();
  const { currency, rate } = useCurrency();
  const [form, setForm] = useState({
    title: "", species: "cattle", breed: "", lot_type: "semen_straw", donor_name: "",
    donor_registration: "", donor_ear_tag: "", pedigree_notes: "", quantity: 10, price_per_unit: 100,
    cryogenic_temp_c: -196, collection_date: "", lab_accreditation: "", image_url: "",
  });
  const [attrs, setAttrs] = useState([{ k: "", v: "" }]);
  const [attrList, setAttrList] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [customBreed, setCustomBreed] = useState("");
  const [certs, setCerts] = useState([]);
  const [certUploading, setCertUploading] = useState(false);
  const [certProgress, setCertProgress] = useState(0);
  const [aiDrafting, setAiDrafting] = useState(false);
  const [pedParsing, setPedParsing] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const uploadCerts = async (files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "health_cert");
      setCertUploading(true);
      setCertProgress(0);
      try {
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => setCertProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
        });
        const isImg = (file.type || "").startsWith("image/");
        setCerts((c) => [...c, { id: data.id, filename: data.filename, size: file.size, preview: isImg ? URL.createObjectURL(file) : null }]);
        toast.success(`${file.name} uploaded`);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setCertUploading(false); }
    }
  };

  const draftWithAI = async () => {
    setAiDrafting(true);
    setForm((f) => ({ ...f, pedigree_notes: "" }));
    try {
      const genetic_attributes = {};
      attrs.forEach(a => { if (a.k) genetic_attributes[a.k] = a.v; });
      await streamAI("/ai/listing-assist", {
        method: "POST",
        body: { title: form.title, species: form.species, breed: form.breed === "__custom__" ? customBreed.trim() : form.breed, donor_name: form.donor_name, genetic_attributes },
        onDelta: (t) => setForm((f) => ({ ...f, pedigree_notes: f.pedigree_notes + t })),
      });
      toast.success("Pedigree notes drafted — review before publishing");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiDrafting(false); }
  };

  const parsePedigreeFile = async (f) => {
    if (!f) return;
    setPedParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/lots/parse-pedigree", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const p = data.parsed || {};
      setForm((prev) => ({
        ...prev,
        title: p.title || prev.title,
        species: p.species || prev.species,
        breed: p.breed ? (breeds.some(b => b.breed === p.breed) ? p.breed : "__custom__") : prev.breed,
        donor_name: p.donor_name || prev.donor_name,
        donor_registration: p.donor_registration || prev.donor_registration,
        donor_ear_tag: p.donor_ear_tag || prev.donor_ear_tag,
        collection_date: p.collection_date || prev.collection_date,
        lab_accreditation: p.lab_accreditation || prev.lab_accreditation,
        pedigree_notes: p.pedigree_notes || prev.pedigree_notes,
      }));
      if (p.breed && !breeds.some(b => b.breed === p.breed)) setCustomBreed(p.breed);
      if (p.genetic_attributes && Object.keys(p.genetic_attributes).length)
        setAttrs(Object.entries(p.genetic_attributes).map(([k, v]) => ({ k, v: String(v) })).concat([{ k: "", v: "" }]));
      toast.success("Pedigree parsed — review the pre-filled fields");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPedParsing(false); }
  };

  const bulkDryRun = async () => {
    if (!bulkFile) { toast.error("Choose a CSV or Excel file first"); return; }
    setBulkBusy(true);
    try {
      const fd = new FormData(); fd.append("file", bulkFile);
      const { data } = await api.post("/lots/bulk?dry_run=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  const bulkCommit = async () => {
    setBulkBusy(true);
    try {
      const fd = new FormData(); fd.append("file", bulkFile);
      const { data } = await api.post("/lots/bulk?dry_run=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data); setBulkPreview(null); setBulkFile(null);
      toast.success(`${data.created_count} lots created`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  useEffect(() => { api.get("/reference/species").then(r => setAttrList(r.data.attributes[form.species] || [])); }, [form.species]);
  useEffect(() => { api.get("/heritage-breeds", { params: { species: form.species } }).then(r => setBreeds(r.data.breeds)); }, [form.species]);

  const on = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const setAttrK = (i, v) => { const c = [...attrs]; c[i].k = v; setAttrs(c); };
  const setAttrV = (i, v) => { const c = [...attrs]; c[i].v = v; setAttrs(c); };
  const addAttr = () => setAttrs([...attrs, { k: "", v: "" }]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const genetic_attributes = {};
      attrs.forEach(a => { if (a.k) genetic_attributes[a.k] = a.v; });
      const breed = form.breed === "__custom__" ? customBreed.trim() : form.breed;
      if (!breed) { toast.error("Enter your breed name"); return; }
      const payload = { ...form, breed, quantity: parseInt(form.quantity), price_per_unit: parseFloat(form.price_per_unit) / (rate || 1), cryogenic_temp_c: parseFloat(form.cryogenic_temp_c), genetic_attributes, health_cert_ids: certs.map(c => c.id) };
      const { data } = await api.post("/lots", payload);
      toast.success(`Lot ${data.lot_id} created`);
      nav("/seller");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <h1 className="heading text-3xl font-extrabold tracking-tight">New lot</h1>
      <div className="surface-card p-5 mt-6" data-testid="bulk-import-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="heading font-semibold">Bulk import lots</div>
            <div className="text-xs text-stone-500 mt-0.5">Upload a CSV/Excel with one lot per row — <a href={`${API_URL}/lots/bulk-template`} data-testid="bulk-template-link" className="underline">download the template</a>. Columns prefixed attr_ become genetic attributes.</div>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" data-testid="bulk-preview-btn" variant="outline" disabled={bulkBusy || !bulkFile} onClick={bulkDryRun}>{bulkBusy ? "Checking…" : "Validate"}</Button>
            {bulkPreview && <Button type="button" data-testid="bulk-confirm-btn" className="btn-earth" disabled={bulkBusy || bulkPreview.rows_valid === 0} onClick={bulkCommit}>Import {bulkPreview.rows_valid} lot(s)</Button>}
          </div>
        </div>
        <FileDrop testid="bulk-upload-input" accept=".csv,.xlsx,.xls"
                  hint={bulkFile ? `Selected: ${bulkFile.name}` : "Drop a CSV or Excel file here, or click to browse"}
                  onFiles={(fs) => { setBulkFile(fs[0]); setBulkPreview(null); setBulkResult(null); }} />
        {bulkPreview && (
          <div data-testid="bulk-preview" className="mt-3 border border-stone-200 rounded-md overflow-hidden">
            <div className="text-xs px-3 py-2 bg-stone-50 mono">{bulkPreview.rows_total} rows · {bulkPreview.rows_valid} valid</div>
            <table className="data-table">
              <tbody>
                {bulkPreview.rows.slice(0, 8).map((r) => (
                  <tr key={r.row} data-testid={`bulk-row-${r.row}`}>
                    <td className="mono text-xs w-14">#{r.row}</td>
                    <td className="text-xs">{r.title || "—"}</td>
                    <td className="text-xs">{r.errors.length ? <span className="text-red-600">{r.errors.join(", ")}</span> : <span className="text-emerald-700">OK</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {bulkResult && (
          <div data-testid="bulk-result" className="mt-3 text-xs mono bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
            Created {bulkResult.created_count} lot(s): {bulkResult.created.join(", ")}
          </div>
        )}
      </div>
      <form onSubmit={submit} className="surface-card p-6 mt-6 space-y-4">
        <div className="border border-dashed border-stone-300 rounded-md p-4 bg-stone-50/50">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="heading font-semibold text-sm">Auto-fill from pedigree file</div>
              <div className="text-xs text-stone-500 mt-0.5">PDF, image, CSV or TXT — AI extracts donor, registration, lineage and genetic attributes into the form below.</div>
            </div>
            <div className="w-full max-w-md">
              <FileDrop testid="pedigree-upload-input" accept=".pdf,.csv,.txt,.png,.jpg,.jpeg,.webp" disabled={pedParsing}
                        busy={pedParsing} busyLabel="AI is parsing your pedigree…" busyTestid="pedigree-parsing-indicator"
                        hint="Drop a pedigree file here, or click to browse" onFiles={(fs) => parsePedigreeFile(fs[0])} />
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Title</Label><Input data-testid="newlot-title" required value={form.title} onChange={on("title")} /></div>
          <div>
            <Label>Species</Label>
            <Select value={form.species} onValueChange={on("species")}>
              <SelectTrigger data-testid="newlot-species"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["cattle","equine","sheep","swine","goat","canine","camel","poultry"].map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Breed</Label>
            <Select value={form.breed} onValueChange={on("breed")}>
              <SelectTrigger data-testid="newlot-breed"><SelectValue placeholder="Select breed" /></SelectTrigger>
              <SelectContent>
                {breeds.map(b => <SelectItem key={b.breed} value={b.breed}>{b.breed} — {b.status}</SelectItem>)}
                <SelectItem value="__custom__" data-testid="newlot-breed-custom">My breed is not listed — add it</SelectItem>
              </SelectContent>
            </Select>
            {form.breed === "__custom__" && (
              <Input data-testid="newlot-custom-breed" className="mt-2" placeholder="Enter your breed name (it will be added to the breed list)" value={customBreed} onChange={(e) => setCustomBreed(e.target.value)} />
            )}
          </div>
          <div>
            <Label>Lot type</Label>
            <Select value={form.lot_type} onValueChange={on("lot_type")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="semen_straw">Semen straw</SelectItem>
                <SelectItem value="embryo">Embryo</SelectItem>
                <SelectItem value="oocyte">Oocyte</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Donor name</Label><Input data-testid="newlot-donor-name" required value={form.donor_name} onChange={on("donor_name")} /></div>
          <div><Label>Donor registration #</Label><Input value={form.donor_registration} onChange={on("donor_registration")} /></div>
          <div><Label>Ear tag (USDA)</Label><Input value={form.donor_ear_tag} onChange={on("donor_ear_tag")} /></div>
          <div><Label>Quantity</Label><Input type="number" min={1} required value={form.quantity} onChange={on("quantity")} /></div>
          <div>
            <Label>Price per unit ({currency})</Label>
            <Input data-testid="newlot-price" type="number" step="0.01" min={0} required value={form.price_per_unit} onChange={on("price_per_unit")} />
            {currency !== "USD" && form.price_per_unit && (
              <div className="text-xs mono text-stone-500 mt-1" data-testid="newlot-usd-conv">= ${(parseFloat(form.price_per_unit) / (rate || 1)).toFixed(2)} USD (live rate)</div>
            )}
          </div>
          <div>
            <Label>Collection date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" data-testid="newlot-collection-date" className="w-full justify-start text-left font-normal mt-1">
                  <CalendarIcon className="h-4 w-4 mr-2 text-stone-400" />
                  {form.collection_date || <span className="text-stone-400">Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" initialFocus
                          selected={form.collection_date ? new Date(form.collection_date + "T00:00:00") : undefined}
                          onSelect={(d) => setForm((f) => ({ ...f, collection_date: d ? format(d, "yyyy-MM-dd") : "" }))} />
              </PopoverContent>
            </Popover>
          </div>
          <div><Label>Lab accreditation</Label><Input value={form.lab_accreditation} onChange={on("lab_accreditation")} /></div>
          <div className="md:col-span-2">
            <Label>Health certificates / pedigree docs (PDF, JPG, PNG — max {MAX_FILE_MB} MB each)</Label>
            <FileDrop testid="newlot-cert-upload" accept=".pdf,.png,.jpg,.jpeg,.webp" multiple disabled={certUploading}
                      busy={certUploading} busyLabel={`Uploading… ${certProgress}%`}
                      hint="Drop certificates here, or click to browse — you can add several" onFiles={uploadCerts} />
            {certs.length > 0 && (
              <ul data-testid="newlot-cert-list" className="mt-2 space-y-1.5">
                {certs.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 border border-stone-200 rounded-md px-3 py-2 bg-white text-xs mono">
                    {c.preview
                      ? <img src={c.preview} alt="" className="h-9 w-9 rounded object-cover border border-stone-200 shrink-0" />
                      : <FileText className="h-4 w-4 text-stone-400 shrink-0" />}
                    <span className="truncate">{c.filename}</span>
                    <span className="text-stone-400 shrink-0">{formatSize(c.size)}</span>
                    <button type="button" data-testid={`newlot-cert-remove-${c.id}`} className="ml-auto text-red-600 hover:underline shrink-0"
                            onClick={() => { if (c.preview) URL.revokeObjectURL(c.preview); setCerts(certs.filter((x) => x.id !== c.id)); }}>remove</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="md:col-span-2"><Label>Cover image URL</Label><Input value={form.image_url} onChange={on("image_url")} placeholder="https://…" /></div>
          <div className="md:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Pedigree notes</Label>
              <Button type="button" data-testid="newlot-ai-draft-btn" variant="outline" size="sm" disabled={aiDrafting || !form.title || !form.breed} onClick={draftWithAI}>
                {aiDrafting ? "Drafting…" : "✦ Draft with Claude"}
              </Button>
            </div>
            <Textarea data-testid="newlot-pedigree" className="mt-1" value={form.pedigree_notes} onChange={on("pedigree_notes")} />
          </div>
        </div>

        <div>
          <Label>Genetic attributes</Label>
          <div className="text-xs text-stone-500 mb-2">Suggested: {attrList.join(", ")}</div>
          <div className="space-y-2">
            {attrs.map((a, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <Input placeholder="Attribute (e.g., MARB)" value={a.k} onChange={(e) => setAttrK(i, e.target.value)} />
                <Input placeholder="Value" value={a.v} onChange={(e) => setAttrV(i, e.target.value)} />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addAttr}>+ Add attribute</Button>
          </div>
        </div>

        <div className="pt-4 border-t flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => nav("/seller")}>Cancel</Button>
          <Button data-testid="newlot-submit-btn" type="submit" className="btn-earth">Publish lot</Button>
        </div>
      </form>
    </div>
  );
}

export default function SellerPortal() {
  return (
    <Routes>
      <Route index element={<Dashboard />} />
      <Route path="*" element={<Navigate to="/seller" replace />} />
      <Route path="new-lot" element={<NewLot />} />
    </Routes>
  );
}
